import { z } from 'zod';
import { idSchema, ratioSchema, signedRatioSchema, timestampSchema } from './common.js';
import { TAX_RESIDENCIES } from './reference.js';
/**
 * Lot 6 — deux calculs distincts, chacun fidèle à son classeur d'origine, jamais unifiés
 * en un seul modèle de saisie : le comparateur (feuille « Fiscalité avancée », mono-année,
 * loyer en part du prix) et la projection (feuille « MMI », 25 ans, loyer mensuel saisi).
 */
export const SIMULATION_KINDS = ['regime-comparison', 'projection', 'deal'];
/** Comparateur mono-année des six régimes — champs de `RegimeComparisonInput` (`@app/finance`). */
export const regimeComparisonInputSchema = z.object({
    taxResidency: z.enum(TAX_RESIDENCIES),
    purchasePrice: z.number().positive(),
    notaryFees: z.number().nonnegative(),
    works: z.number().nonnegative(),
    buildingDepreciationYears: z.number().int().min(1).max(100),
    worksDepreciationYears: z.number().int().min(1).max(100),
    annualRentRate: ratioSchema,
    annualPriceGrowth: signedRatioSchema,
    holdingYears: z.number().int().min(1).max(99),
    loanAnnualRate: z.number().min(0).max(0.25),
    loanYears: z.number().int().min(1).max(60),
    annualCondo: z.number().nonnegative(),
    annualPropertyTax: z.number().nonnegative(),
    annualPno: z.number().nonnegative(),
    accountingLmnp: z.number().nonnegative(),
    accountingSciIs: z.number().nonnegative(),
    cfeShareOfPropertyTax: ratioSchema,
    personalTaxRate: ratioSchema,
    corporateTaxRate: ratioSchema,
    microFoncierAllowance: ratioSchema,
    microBicAllowance: ratioSchema,
    /** Plafond de loyers du micro-BIC ; lu par `mmi-2026-corrected` seul. */
    microBicCeiling: z.number().nonnegative().optional(),
});
/** Projection à 25 ans — champs de `MmiPropertyInputs` (`@app/finance`) ; les hypothèses
 * globales (inflation, amortissement…) viennent de `GET /assumptions`, pas d'ici. */
export const projectionInputSchema = z.object({
    taxResidency: z.enum(TAX_RESIDENCIES),
    surfaceM2: z.number().positive(),
    holdingYears: z.number().int().min(1).max(35),
    purchasePrice: z.number().positive(),
    notaryFees: z.number().nonnegative(),
    works: z.number().nonnegative(),
    plan: z.number().nonnegative(),
    furniture: z.number().nonnegative(),
    agencyFees: z.number().nonnegative(),
    guaranteeFees: z.number().nonnegative(),
    monthlyRent: z.number().nonnegative(),
    vacancyMonths: z.number().min(0).max(12),
    monthlyCondo: z.number().nonnegative(),
    monthlyPropertyTax: z.number().nonnegative(),
    monthlyCfe: z.number().nonnegative(),
    monthlyPno: z.number().nonnegative(),
    monthlyAccounting: z.number().nonnegative(),
    monthlyEnergy: z.number().nonnegative(),
    monthlyInternet: z.number().nonnegative(),
    managementFeeRate: ratioSchema,
    debt: z.number().nonnegative(),
    loanAnnualRate: z.number().min(0).max(0.25),
    loanYears: z.number().int().min(0).max(60),
    loanInsuranceMonthly: z.number().nonnegative(),
});
/**
 * Achat-revente (lot 16) — champs de `DealInput` (`@app/finance`), moteur `deal-2026`. Fiscalité
 * indépendante de celle des deux autres genres (D10) : ni résidence fiscale, ni hypothèses globales.
 * L'opérateur est verrouillé sur « professionnel » : le régime de faveur (art. 1115 CGI) est ce qui
 * distingue le marchand de biens, pas une option d'ergonomie.
 */
export const dealInputSchema = z.object({
    operator: z.literal('professional'),
    registrationRate: ratioSchema.optional(),
    purchasePrice: z.number().positive(),
    surfaceM2: z.number().positive(),
    works: z.number().nonnegative(),
    contingencyRate: ratioSchema,
    commissionRate: ratioSchema,
    financingRate: z.number().min(0).max(0.25),
    equityShare: ratioSchema,
    carryMonths: z.number().int().min(1).max(60),
    monthlyCarryingCharges: z.number().nonnegative(),
    resalePrices: z.object({
        pessimistic: z.number().nonnegative(),
        realistic: z.number().nonnegative(),
        optimistic: z.number().nonnegative(),
    }),
    vatRegime: z.enum(['none', 'margin']),
    vatRate: ratioSchema,
    corporateTaxRate: ratioSchema,
    reducedCorporateTaxRate: z.boolean(),
});
const simulationBase = z.object({
    label: z.string().trim().min(1).max(120),
    /** Moteur de `@app/finance` au moment de l'enregistrement : `mmi-2026` ou `mmi-2026-corrected`. */
    engineVersion: z.string().min(1).max(40),
});
/** Corps de `POST /simulations` : jamais de modification ensuite, une nouvelle ligne à chaque enregistrement. */
export const simulationInputSchema = z.discriminatedUnion('kind', [
    simulationBase.extend({ kind: z.literal('regime-comparison'), input: regimeComparisonInputSchema }),
    simulationBase.extend({ kind: z.literal('projection'), input: projectionInputSchema }),
    simulationBase.extend({ kind: z.literal('deal'), input: dealInputSchema }),
]);
export const simulationSchema = z.discriminatedUnion('kind', [
    simulationBase.extend({
        kind: z.literal('regime-comparison'),
        input: regimeComparisonInputSchema,
        id: idSchema,
        createdAt: timestampSchema,
    }),
    simulationBase.extend({
        kind: z.literal('projection'),
        input: projectionInputSchema,
        id: idSchema,
        createdAt: timestampSchema,
    }),
    simulationBase.extend({
        kind: z.literal('deal'),
        input: dealInputSchema,
        id: idSchema,
        createdAt: timestampSchema,
    }),
]);
//# sourceMappingURL=simulation.js.map